import { Card, Empty, List, Pagination, Spin, Typography } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { Loader } from 'components/common/Loader';
import { BusinessListItem } from 'components/molecules/BusinessListItem';
import { useTheme } from 'context/ThemeContext';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useGetBusinessNameConatinsQuery } from 'redux/services/abbyCadabby/abbyCadabbyApi';
import { setCustomerSearch } from 'redux/slices/businessListSlice';
import { useAppSelector } from 'redux/store';
import { useRenderPerformanceMonitor } from 'utils/performanceUtils';

// Constants for pagination
const PAGE_SIZE = 20;

interface BusinessListProps {
  onCustomerSelect?: () => void;
}

export const BusinessList = memo(({ onCustomerSelect }: BusinessListProps): JSX.Element => {
  // Monitor render performance
  useRenderPerformanceMonitor('BusinessList');

  const dispatch = useDispatch();
  const { navSearchValue } = useAppSelector((state) => state.businessList);
  const { abbyCadabbyParams } = useAppSelector((state) => state);
  const { isDarkMode } = useTheme();
  const { xs, sm, md } = useBreakpoint();
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = xs || sm;

  const { data, isLoading, isFetching } = useGetBusinessNameConatinsQuery(abbyCadabbyParams);

  // Clears state on unmount
  useEffect(() => {
    return (): void => {
      dispatch(setCustomerSearch(undefined));
    };
  }, [dispatch]);

  const businessList = data?.data;

  // Optimized filteredData calculation with memoization
  const filteredData = useMemo(() => {
    if (!businessList) return [];

    // Filter businesses based on search criteria
    return businessList
      .map((business) => {
        // Only process divisions once
        const filteredDivisions = business.divisions.filter(
          (division) => !navSearchValue || division.name.toLowerCase().includes((navSearchValue || '').toLowerCase()) || division.erpId.toLowerCase().includes((navSearchValue || '').toLowerCase())
        );

        return {
          ...business,
          divisions: filteredDivisions
        };
      })
      .filter((business) => !navSearchValue || business.divisions.length > 0);
  }, [businessList, navSearchValue]);

  // Paginate the data
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredData.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredData, currentPage]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    // Scroll to top of the list when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Calculate total items for pagination
  const totalItems = useMemo(() => filteredData.length, [filteredData]);

  /* ******************** Render ******************** */
  return (
    <div className="business-list-wrapper" style={{ maxWidth: 1920, margin: '0 0', padding: isMobile ? '0 0px' : '0 0px' }}>
      <Card
        className="business-list-container"
        bordered={false}
        style={{
          borderRadius: 8,
          padding: '0px 0px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          background: isDarkMode ? 'rgba(57, 56, 60, 0.51)' : 'rgb(229, 232, 238)',
          opacity: isDarkMode ? 0.8 : 1,
          border: isDarkMode ? '1px solid rgb(69, 68, 76)' : '1px solid #e8e8e8'
        }}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
            <Spin indicator={<Loader isDarkMode={isDarkMode} transparent componentLoader loadingText="Loading customers..." />} />
          </div>
        ) : (
          <>
            {paginatedData.length === 0 ? (
              <Empty
                description={
                  <Typography.Text type="secondary" style={{ fontSize: 16, color: isDarkMode ? '#fff' : '#000' }}>
                    {navSearchValue ? 'No customers match your search' : 'No customers available'}
                  </Typography.Text>
                }
                style={{
                  padding: '40px 0',
                  background: isDarkMode ? 'rgba(57, 56, 60, 0.51)' : '#f5f5f5',
                  borderRadius: 8
                }}
              />
            ) : (
              <div style={{ position: 'relative' }}>
                {isFetching && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(255, 255, 255, 0.6)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      zIndex: 1,
                      borderRadius: 8
                    }}>
                    <Spin indicator={<Loader isDarkMode={isDarkMode} transparent componentLoader />} />
                  </div>
                )}
                <List
                  dataSource={paginatedData}
                  renderItem={(item): JSX.Element => <BusinessListItem business={item} onCustomerSelect={onCustomerSelect} />}
                  rowKey={(item: any): string => item.id}
                  style={{
                    marginTop: 8,
                    background: 'transparent',
                    borderRadius: 8,
                    padding: 8
                  }}
                />
              </div>
            )}
          </>
        )}

        {!isLoading && !isFetching && totalItems > PAGE_SIZE && (
          <Pagination
            current={currentPage}
            onChange={handlePageChange}
            total={totalItems}
            pageSize={PAGE_SIZE}
            showSizeChanger={false}
            style={{
              marginTop: 24,
              textAlign: 'center',
              padding: '8px 0'
            }}
            responsive
          />
        )}
      </Card>
    </div>
  );
});
